import type { ReactNode } from 'react'
import { useEmployeeTransactionItems } from './useEmployeeTransactionItems'
import { formatEmployeeValue, type EmployeePerformanceQueryContext } from '../../lib/employeePerformance'
import {
    describeEmployeeTransactionItemsError,
    isTransactionItemLeftOut,
    transactionItemDetails,
    transactionItemDiscountKind,
    type EmployeeTransactionItem,
} from '../../lib/employeeTransactions'

// ── Employee Performance widget: the items on a receipt ─────────────────
//
// The row the drill-down (EmployeeTransactionsModal) opens under a receipt: one cell across every
// column, holding the lines on the sale in the order the till saved them. The drill-down mounts it
// only while its receipt is open, so the items are asked for when the receipt is first opened and
// never before; react-query keeps them, so a receipt closed and opened again shows them at once.
//
// Every line is listed. Under GROSS a returned line isn't in the receipt's figures: it is dimmed and
// marked as not counted, and the closing line adds up the counted lines alone, so it reads exactly as
// the receipt's own row — units, sales and discounts. (GROSS doesn't count a line rung at zero either,
// but that line changes no figure, so it is listed like any other.) No line's cost, and nothing about
// the customer.
//
// Each row and cell names its role outright. On a narrow screen the stylesheet lays the rows out as
// grids — each item over its figures — and a browser can take a row that isn't laid out as a table
// row for no row at all.

export interface TransactionItemsRowProps {
    /** The row's id: its receipt's button names it in aria-controls. */
    id: string
    /** The drill-down's view: the widget's own period and filters, and the page's query key. */
    query: EmployeePerformanceQueryContext
    employeeId: string
    transactionId: string
    /** What the list is called: 'Items on receipt #1178789'. */
    label: string
    /** Every column of the receipts table, the disclosure column among them. */
    colSpan: number
}

const money = (n: number) => formatEmployeeValue('currencyCents', n)
const whole = (n: number) => formatEmployeeValue('int', n)

function ItemLine({ item }: { item: EmployeeTransactionItem }) {
    const details = transactionItemDetails(item)
    const kind = transactionItemDiscountKind(item)
    const leftOut = isTransactionItemLeftOut(item)
    return (
        <tr role="row" className={leftOut ? 'is-uncounted' : undefined}>
            {/* The row header, read out with each of the line's figures. */}
            <th scope="row" role="rowheader" className="emp-txn-item">
                <span className="emp-txn-item-line">
                    <span className={item.description ? 'emp-txn-item-name' : 'emp-txn-item-name is-unknown'}>
                        {item.description ?? 'No description'}
                    </span>
                    {item.isReturn && <span className="emp-perf-tag emp-txn-return">Return</span>}
                    {leftOut && <span className="emp-perf-tag emp-txn-uncounted">Not in GROSS figures</span>}
                </span>
                {details && <span className="emp-txn-item-sub">{details}</span>}
            </th>
            <td role="cell" className="emp-perf-num">{whole(item.quantity)}</td>
            <td role="cell" className="emp-perf-num">{money(item.unitPrice)}</td>
            <td role="cell" className="emp-perf-num">
                {money(item.discount)}
                {kind && <span className="emp-txn-item-kind">{kind}</span>}
            </td>
            <td role="cell" className="emp-perf-num">{money(item.netSales)}</td>
        </tr>
    )
}

export default function TransactionItemsRow({ id, query, employeeId, transactionId, label, colSpan }: TransactionItemsRowProps) {
    const { data, error } = useEmployeeTransactionItems(query, employeeId, transactionId)

    let body: ReactNode
    if (error) {
        body = <p className="emp-txn-items-state" role="status">{describeEmployeeTransactionItemsError(error)}</p>
    } else if (!data) {
        body = <p className="emp-txn-items-state" role="status">Loading items…</p>
    } else if (data.items.length === 0) {
        body = <p className="emp-txn-items-state" role="status">No items on this receipt.</p>
    } else {
        const { totals } = data
        body = (
            <table className="emp-txn-items" aria-label={label}>
                <thead>
                    <tr role="row">
                        <th scope="col" role="columnheader">Item</th>
                        <th scope="col" role="columnheader" className="emp-perf-num" title="How many. A returned item counts below zero.">Qty</th>
                        <th scope="col" role="columnheader" className="emp-perf-num" title="The price for one, before any discount.">Price</th>
                        <th
                            scope="col"
                            role="columnheader"
                            className="emp-perf-num"
                            title="The discount on the line: staff-given when staff recorded a reason for it, promotional when not (system pricing, multibuys)."
                        >
                            Discount
                        </th>
                        <th
                            scope="col"
                            role="columnheader"
                            className="emp-perf-num"
                            title="What the line sold for: the price less its discount, times the quantity."
                        >
                            Net
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {data.items.map(item => <ItemLine key={item.lineId} item={item} />)}
                </tbody>
                <tfoot>
                    {/* The counted lines alone: the receipt's own row. */}
                    <tr role="row">
                        <th scope="row" role="rowheader">
                            <span className="emp-perf-total-label">Total</span>
                            {data.items.some(isTransactionItemLeftOut) && <span className="emp-txn-item-sub">Returns left out</span>}
                        </th>
                        <td role="cell" className="emp-perf-num">{whole(totals.units)}</td>
                        <td role="cell" />
                        <td role="cell" className="emp-perf-num">{money(totals.discounts)}</td>
                        <td role="cell" className="emp-perf-num">{money(totals.netSales)}</td>
                    </tr>
                </tfoot>
            </table>
        )
    }

    return (
        <tr id={id} className="emp-txn-items-row">
            <td colSpan={colSpan} className="emp-txn-items-cell">{body}</td>
        </tr>
    )
}
